import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
} from "lucide-react";
import type { ReactElement } from "react";
import { PtzButton } from "./PtzButton.tsx";
import type { PtzDir } from "../../lib/usePtzContinuousControl.ts";

const ICON_SIZE = 15;
const ICON_STROKE = 2.25;

type PanTiltDir = Extract<PtzDir, "up" | "down" | "left" | "right">;

export type PtzDpadPlacement = "left" | "right" | "bottom-right" | "top" | "bottom";
export type PtzDpadSize = "default" | "compact";

export type PtzGamepadDpadProps = {
  counts: Record<PanTiltDir, number>;
  isFull: (dir: PanTiltDir) => boolean;
  atLimit: Record<PanTiltDir, boolean>;
  operable: boolean;
  onPressStart: (dir: PanTiltDir) => void;
  onPressEnd: () => void;
  /** Where the cross pad sits on the PTZ view (default bottom center). */
  placement?: PtzDpadPlacement;
  size?: PtzDpadSize;
};

function DirButton({
  dir,
  ariaLabel,
  className,
  icon,
  counts,
  isFull,
  atLimit,
  operable,
  onPressStart,
  onPressEnd,
}: {
  dir: PanTiltDir;
  ariaLabel: string;
  className: string;
  icon: ReactElement;
  counts: Record<PanTiltDir, number>;
  isFull: (dir: PanTiltDir) => boolean;
  atLimit: Record<PanTiltDir, boolean>;
  operable: boolean;
  onPressStart: (dir: PanTiltDir) => void;
  onPressEnd: () => void;
}): ReactElement {
  const count = counts[dir];
  return (
    <PtzButton
      className={`ds-ptz-dpad__btn ${className}`}
      ariaLabel={ariaLabel}
      count={count}
      full={isFull(dir)}
      disabled={atLimit[dir] || !operable}
      ariaSuffix={count > 0 ? "moving" : undefined}
      onPressStart={() => onPressStart(dir)}
      onPressEnd={onPressEnd}
    >
      {icon}
    </PtzButton>
  );
}

export function PtzGamepadDpad({
  counts,
  isFull,
  atLimit,
  operable,
  onPressStart,
  onPressEnd,
  placement = "bottom",
  size = "default",
}: PtzGamepadDpadProps): ReactElement {
  const dpadClass = [
    "ds-ptz-dpad",
    placement === "left"
      ? "ds-ptz-dpad--left"
      : placement === "right" || placement === "bottom-right"
        ? "ds-ptz-dpad--bottom-right"
        : placement === "top"
          ? "ds-ptz-dpad--top"
          : "ds-ptz-dpad--bottom",
    size === "compact" ? "ds-ptz-dpad--compact" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={dpadClass} role="group" aria-label="PTZ direction pad">
      <DirButton
        dir="up"
        ariaLabel="Tilt up"
        className="ds-ptz-dpad__btn--up"
        icon={
          <ChevronUp size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
        }
        counts={counts}
        isFull={isFull}
        atLimit={atLimit}
        operable={operable}
        onPressStart={onPressStart}
        onPressEnd={onPressEnd}
      />
      <DirButton
        dir="left"
        ariaLabel="Pan left"
        className="ds-ptz-dpad__btn--left"
        icon={
          <ChevronLeft size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
        }
        counts={counts}
        isFull={isFull}
        atLimit={atLimit}
        operable={operable}
        onPressStart={onPressStart}
        onPressEnd={onPressEnd}
      />
      <span className="ds-ptz-dpad__hub" aria-hidden />
      <DirButton
        dir="right"
        ariaLabel="Pan right"
        className="ds-ptz-dpad__btn--right"
        icon={
          <ChevronRight size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
        }
        counts={counts}
        isFull={isFull}
        atLimit={atLimit}
        operable={operable}
        onPressStart={onPressStart}
        onPressEnd={onPressEnd}
      />
      <DirButton
        dir="down"
        ariaLabel="Tilt down"
        className="ds-ptz-dpad__btn--down"
        icon={
          <ChevronDown size={ICON_SIZE} strokeWidth={ICON_STROKE} aria-hidden />
        }
        counts={counts}
        isFull={isFull}
        atLimit={atLimit}
        operable={operable}
        onPressStart={onPressStart}
        onPressEnd={onPressEnd}
      />
    </div>
  );
}
